import {
  useCreateSubmissionPresignedUrlMutation,
  useCreateSubmissionMutation,
  EngagementSubmissionData,
} from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { uploadFile } from '@/utils/uploadFile';
import { scaleImage } from '@/utils/imageScaling';
import { useCallback, useState } from 'react';
import { TypedSubmission } from '@/utils/engagementTypeUtils';
import { useDeviceSignalVariables } from '@/hooks/useDeviceIdentity';

const logger = createLogger('useCreateSubmission');

export interface Upload<T extends EngagementSubmissionData> {
  field: keyof T;
  file: File | null | undefined;
  type: 'image';
  maxWidth?: number;
  maxHeight?: number;
}

export function useCreateSubmission<T extends EngagementSubmissionData>(
  enableImageScaling: boolean = true,
) {
  const [createPresignedUrl] = useCreateSubmissionPresignedUrlMutation();
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgresses, setUploadProgresses] = useState<number[]>([]);
  const [succeeded, setSucceeded] = useState(false);
  const [createSubmissionMutation] = useCreateSubmissionMutation();
  const { variables: deviceSignals } = useDeviceSignalVariables();

  const createSubmission = useCallback(
    async ({
      engagementId,
      uploads,
      data: submissionData,
      onSuccess,
    }: {
      engagementId: string;
      uploads?: Upload<T>[];
      data: Partial<T>;
      onSuccess?: (submission: TypedSubmission<T>) => void;
    }): Promise<TypedSubmission<T> | { errors: string[] }> => {
      setLoading(true);
      setErrors([]);
      setUploadProgresses(([] as number[]).fill(0, 0, uploads?.length ?? 0));
      setSucceeded(false);
      try {
        if (uploads && uploads.length > 0) {
          // Scale images if maxWidth/maxHeight constraints are specified and scaling is enabled
          const scaledUploads = enableImageScaling
            ? await Promise.all(
                uploads.map(async (upload) => {
                  if (upload.file && (upload.maxWidth || upload.maxHeight)) {
                    try {
                      const scaledFile = await scaleImage(
                        upload.file,
                        upload.maxWidth,
                        upload.maxHeight,
                      );
                      return { ...upload, file: scaledFile };
                    } catch (error) {
                      logger.error(
                        `Error scaling image for ${String(upload.field)}:`,
                        error,
                      );
                      // Continue with original file if scaling fails
                      return upload;
                    }
                  }
                  return upload;
                }),
              )
            : uploads;

          // request all presigned URLs
          const presignedUrlResults = await Promise.all(
            scaledUploads.map(async (upload) => {
              if (!upload.file?.type) {
                return {
                  errors: ['No file type'],
                };
              }
              try {
                return await createPresignedUrl({
                  variables: {
                    engagementId,
                    mimeType: upload.file?.type ?? '',
                    ...deviceSignals,
                  },
                });
              } catch (e) {
                logger.error(
                  `Error creating presigned URL for ${String(upload.field)}:`,
                  e,
                );
                return {
                  errors: ['Error creating presigned URL'],
                };
              }
            }),
          );

          // check for errors
          const presignedUrlErrors = presignedUrlResults
            .filter((result) => result.errors && result.errors.length)
            .map((result) => result.errors);

          if (presignedUrlErrors.length > 0) {
            const errors = presignedUrlErrors
              .filter(Boolean)
              .map((e) => e!.join(', '));
            setErrors(errors);
            setLoading(false);
            return { errors };
          }

          // proceed with upload, and report progress
          const uploadErrors: string[] = [];
          await Promise.all(
            presignedUrlResults.map(async (presignedUrlResult, index) => {
              const upload = scaledUploads[index];
              if (
                'errors' in presignedUrlResult ||
                !presignedUrlResult.data?.createSubmissionPresignedUrl ||
                !(upload.file instanceof File)
              ) {
                uploadErrors.push('No presigned URL returned');
                return;
              }
              const { url } =
                presignedUrlResult.data.createSubmissionPresignedUrl;
              await uploadFile({
                file: upload.file,
                presignedUrl: url,
                onProgress: (progress) =>
                  setUploadProgresses((prev) => [
                    ...prev.slice(0, index),
                    progress,
                    ...prev.slice(index + 1),
                  ]),
              }).catch((e) => {
                logger.error(
                  `Error uploading file for ${String(upload.field)}:`,
                  e,
                );
                uploadErrors.push('Error uploading file');
              });
              // strip query string from upload url for destination url
              const destinationUrl = new URL(url);
              destinationUrl.search = '';
              submissionData[upload.field] =
                destinationUrl.toString() as T[keyof T];
            }),
          );

          if (uploadErrors.length > 0) {
            setErrors(uploadErrors);
            setLoading(false);
            return { errors: uploadErrors };
          }
        }

        logger.info('upload complete, submitting Submission: ', submissionData);

        // once upload is complete, submit photo and caption to the backend
        const result = await createSubmissionMutation({
          variables: {
            engagementId,
            data: submissionData,
            ...deviceSignals,
          },
        });

        if (!result.data?.createSubmission) {
          setErrors(['Submission could not be created']);
          setLoading(false);
          return { errors: ['No submission returned'] };
        }

        if (result.errors) {
          const errors = result.errors.map((e) => e.message);
          setErrors(errors);
          setLoading(false);
          return { errors };
        }

        // report success or failure
        setSucceeded(true);
        onSuccess?.(result.data.createSubmission as TypedSubmission<T>);
        return result.data.createSubmission as TypedSubmission<T>;
      } catch (e) {
        const message =
          e && typeof e === 'object' && 'message' in e
            ? String((e as { message: string }).message)
            : 'Unexpected error occurred';
        const friendly = message.includes('disabled for this device')
          ? 'Submissions are disabled for this device.'
          : 'Unexpected error occurred';
        setErrors([friendly]);
        console.error(`submitPhoto failed with error:`);
        console.error(e);
        return {
          errors: [friendly],
        };
      } finally {
        setLoading(false);
      }
    },
    [
      createSubmissionMutation,
      createPresignedUrl,
      enableImageScaling,
      deviceSignals,
    ],
  );

  return {
    createSubmission,
    uploadProgress: Math.min(...uploadProgresses),
    errors,
    loading,
    succeeded,
  };
}
